import type { QueryClient } from '@tanstack/react-query';

import type { StructuredTargetVo } from 'models/milestones';

export const structuredTargetKeys = {
    all: ['structuredTargets', 'all'] as const,
    /** Single normalized `id -> StructuredTargetVo` map for O(1) imperative reads. */
    normalized: ['structuredTargets', 'normalized'] as const,
    byIds: (ids: readonly string[]) => ['structuredTargets', 'byIds', ids] as const,
};

/** Normalized structured-target map stored in the React Query cache. */
export type StructuredTargetMap = Record<string, StructuredTargetVo>;

/**
 * Merges fetched targets into the single `structuredTargetKeys.normalized` map
 * so non-React consumers (plain transform functions, e.g. `marketEntityTransform.ts`) can
 * resolve a target by id via `getCachedStructuredTargetById` without knowing which `byIds`
 * query holds it.
 */
export function mergeIntoNormalizedTargets(queryClient: QueryClient, targets: StructuredTargetVo[]): void {
    if (!targets.length) return;
    queryClient.setQueryData<StructuredTargetMap>(structuredTargetKeys.normalized, (prev) => {
        const next = { ...(prev ?? {}) };
        for (const target of targets) next[target.id] = target;
        return next;
    });
}

/**
 * Seeds structured targets into the normalized cache from an SSR/BFF payload so
 * imperative `getCachedStructuredTargetById` reads resolve immediately.
 */
export function seedStructuredTargetsIntoCache(queryClient: QueryClient, targets: StructuredTargetVo[]): void {
    mergeIntoNormalizedTargets(queryClient, targets);
}

/** Never executes (`enabled: false`); hoisted so every reader shares one identity. */
export const emptyStructuredTargetMap = (): StructuredTargetMap => ({});
